/**
 * Product Countdown Custom Element
 * Renders a block-format countdown (DAYS / HOURS / MINUTES / SECONDS).
 * Accepts data-launch-date (ISO 8601 string). Auto-reloads when countdown hits zero.
 */
(function () {
  'use strict';

  if (window.__intusKlaviyoEmbedInit) return;
  window.__intusKlaviyoEmbedInit = true;

  let klaviyoReloadPromise = null;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function getKlaviyoCompanyIdFromUrl(url) {
    if (!url) return null;
    const queryMatch = url.match(/company_id=([A-Za-z0-9]+)/);
    if (queryMatch) return queryMatch[1];
    const pathMatch = url.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/);
    if (pathMatch) return pathMatch[1];
    return null;
  }

  function getKlaviyoCompanyId() {
    const scripts = document.querySelectorAll('script[src*="klaviyo.com"]');
    for (const script of scripts) {
      const companyId = getKlaviyoCompanyIdFromUrl(script.src);
      if (companyId) return companyId;
    }
    return null;
  }

  function getKlaviyoOnsiteScriptBaseUrl() {
    const existing = document.querySelector('script[src*="static.klaviyo.com/onsite/js"]');
    if (existing) return existing.src.split('?')[0];

    const companyId = getKlaviyoCompanyId();
    if (companyId) {
      return 'https://static.klaviyo.com/onsite/js/' + companyId + '/klaviyo.js';
    }
    return null;
  }

  function klaviyoContainerHasForm(container) {
    return container.children.length > 0 || !!container.querySelector('form, iframe');
  }

  function isDynamicKlaviyoContainer(container) {
    return !!(
      container.closest('#product-quick-view-content') ||
      container.closest('product-quick-view-modal') ||
      container.closest('.product-quick-view-modal__content')
    );
  }

  function reloadKlaviyoOnsiteScript() {
    if (klaviyoReloadPromise) return klaviyoReloadPromise;

    const baseUrl = getKlaviyoOnsiteScriptBaseUrl();
    const companyId = getKlaviyoCompanyId() || getKlaviyoCompanyIdFromUrl(baseUrl);
    if (!baseUrl || !companyId) {
      klaviyoReloadPromise = Promise.resolve(false);
      setTimeout(function () {
        klaviyoReloadPromise = null;
      }, 500);
      return klaviyoReloadPromise;
    }

    klaviyoReloadPromise = new Promise(function (resolve) {
      document.querySelectorAll('script[src*="static.klaviyo.com/onsite/js"]').forEach(function (el) {
        el.remove();
      });

      const script = document.createElement('script');
      script.src = baseUrl + '?company_id=' + companyId + '&t=' + Date.now();
      script.async = true;
      script.onload = function () { resolve(true); };
      script.onerror = function () { resolve(false); };
      document.head.appendChild(script);

      setTimeout(function () { resolve(true); }, 3000);
    }).finally(function () {
      setTimeout(function () {
        klaviyoReloadPromise = null;
      }, 2000);
    });

    return klaviyoReloadPromise;
  }

  function normalizeKlaviyoEmbedContainer(container) {
    if (!container) return null;

    let formId = container.getAttribute('data-klaviyo-form-id');
    if (!formId) {
      const classMatch = Array.from(container.classList).find(function (className) {
        return className.indexOf('klaviyo-form-') === 0;
      });
      if (classMatch) {
        formId = classMatch.replace('klaviyo-form-', '');
        container.setAttribute('data-klaviyo-form-id', formId);
      }
    }
    if (!formId) return null;

    container.classList.add('klaviyo-form-' + formId);
    return container;
  }

  function findHydratedKlaviyoContainer(formId, excludeContainer) {
    const selector = '[data-klaviyo-form-id="' + formId + '"], .klaviyo-form-' + formId;
    return Array.from(document.querySelectorAll(selector)).find(function (el) {
      return el !== excludeContainer && klaviyoContainerHasForm(el);
    });
  }

  function cloneHydratedKlaviyoForm(targetContainer, formId) {
    const source = findHydratedKlaviyoContainer(formId, targetContainer);
    if (!source) return false;

    targetContainer.innerHTML = source.innerHTML;
    return true;
  }

  function pollForKlaviyoForm(container, maxAttempts, intervalMs, onComplete) {
    let attempts = 0;
    const interval = setInterval(function () {
      attempts += 1;

      if (klaviyoContainerHasForm(container)) {
        clearInterval(interval);
        if (onComplete) onComplete(true);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        if (onComplete) onComplete(false);
      }
    }, intervalMs);
  }

  function initKlaviyoEmbedForm(container) {
    const normalized = normalizeKlaviyoEmbedContainer(container);
    if (!normalized || klaviyoContainerHasForm(normalized)) return;

    const formId = normalized.getAttribute('data-klaviyo-form-id');
    const isDynamic = isDynamicKlaviyoContainer(normalized);

    // Klaviyo only hydrates one embed per form ID per page load — clone an existing form when present.
    if (cloneHydratedKlaviyoForm(normalized, formId)) {
      return;
    }

    if (!isDynamic) return;

    reloadKlaviyoOnsiteScript().then(function () {
      pollForKlaviyoForm(normalized, 40, 250, function (found) {
        if (!found && !klaviyoContainerHasForm(normalized)) {
          cloneHydratedKlaviyoForm(normalized, formId);
        }
      });
    });
  }

  function getDynamicKlaviyoEmbedContainers(scope) {
    const root = scope || document;
    const containers = [];

    root.querySelectorAll('.product-countdown__klaviyo[data-klaviyo-form-id], .product-countdown__klaviyo[class*="klaviyo-form-"]').forEach(function (container) {
      if (!isDynamicKlaviyoContainer(container)) return;
      const normalized = normalizeKlaviyoEmbedContainer(container);
      if (normalized) containers.push(normalized);
    });

    return containers;
  }

  function refreshDynamicKlaviyoEmbeds(scope) {
    getDynamicKlaviyoEmbedContainers(scope).forEach(initKlaviyoEmbedForm);
  }

  function initKlaviyoHiddenFields(countdownEl) {
    const container = countdownEl.querySelector('.product-countdown__klaviyo');
    if (!container) return;

    const productTitle = container.getAttribute('data-product-title');
    if (!productTitle) return;

    function setHiddenFields(form) {
      form.querySelectorAll('input[type="hidden"]').forEach(function (input) {
        if (input.name === 'Product Interest') {
          input.value = productTitle;
        }
      });
    }

    const observer = new MutationObserver(function () {
      const form = container.querySelector('form');
      if (form) {
        setHiddenFields(form);
        observer.disconnect();

        new MutationObserver(function () {
          setHiddenFields(form);
        }).observe(form, { childList: true, subtree: true });
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  function onShopifySectionUpdate(event) {
    const target = event && event.target;
    if (!target || typeof target.querySelector !== 'function') return;
    if (target.querySelector('.product-countdown__klaviyo[data-klaviyo-form-id], .product-countdown__klaviyo[class*="klaviyo-form-"]')) {
      refreshDynamicKlaviyoEmbeds(target);
    }
  }

  window.initProductCountdownKlaviyo = function (scope, options) {
    const root = scope || document;
    const requireDynamic = !!(options && options.dynamic);

    root.querySelectorAll('.product-countdown__klaviyo[data-klaviyo-form-id], .product-countdown__klaviyo[class*="klaviyo-form-"]').forEach(function (container) {
      if (requireDynamic && !isDynamicKlaviyoContainer(container)) return;
      initKlaviyoEmbedForm(container);
    });
  };

  window.scheduleDynamicKlaviyoReload = function () {
    reloadKlaviyoOnsiteScript();
  };

  document.addEventListener('shopify:section:load', onShopifySectionUpdate);
  document.addEventListener('shopify:section:select', onShopifySectionUpdate);

  function isThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  if (customElements.get('product-countdown')) return;

  customElements.define('product-countdown', class ProductCountdown extends HTMLElement {
    constructor() {
      super();
      this.intervalId = null;
      this.daysEl = null;
      this.hoursEl = null;
      this.minutesEl = null;
      this.secondsEl = null;
      this.wasCountingDown = false;
    }

    setExpiredDisplay() {
      if (this.daysEl) this.daysEl.textContent = '00';
      if (this.hoursEl) this.hoursEl.textContent = '00';
      if (this.minutesEl) this.minutesEl.textContent = '00';
      if (this.secondsEl) this.secondsEl.textContent = '00';
    }

    stopTimer() {
      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = null;
    }

    connectedCallback() {
      const launchDateStr = this.getAttribute('data-launch-date');
      if (!launchDateStr) return;

      const targetDate = new Date(launchDateStr);
      if (isNaN(targetDate.getTime())) return;

      initKlaviyoHiddenFields(this);

      const klaviyoContainer = this.querySelector('.product-countdown__klaviyo');
      if (klaviyoContainer) {
        initKlaviyoEmbedForm(klaviyoContainer);
      }

      const timer = this.querySelector('.product-countdown__timer');
      if (!timer) return;

      const units = timer.querySelectorAll('.product-countdown__unit');
      if (units.length < 4) return;

      this.daysEl = units[0].querySelector('.product-countdown__unit-value');
      this.hoursEl = units[1].querySelector('.product-countdown__unit-value');
      this.minutesEl = units[2].querySelector('.product-countdown__unit-value');
      this.secondsEl = units[3].querySelector('.product-countdown__unit-value');

      const tick = () => {
        const now = new Date();
        const diff = (targetDate - now) / 1000;

        if (diff <= 0) {
          this.stopTimer();
          this.setExpiredDisplay();

          if (this.wasCountingDown && !isThemeEditor()) {
            window.location.reload();
          }
          return;
        }

        this.wasCountingDown = true;

        const days = Math.floor(diff / 86400);
        const hours = Math.floor((diff % 86400) / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = Math.floor(diff % 60);

        if (this.daysEl) this.daysEl.textContent = pad(days);
        if (this.hoursEl) this.hoursEl.textContent = pad(hours);
        if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
        if (this.secondsEl) this.secondsEl.textContent = pad(seconds);
      };

      tick();
      if (this.wasCountingDown) {
        this.intervalId = setInterval(tick, 1000);
      }
    }

    disconnectedCallback() {
      this.stopTimer();
    }
  });
})();
